import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Download, ImagePlus, Trash2, X } from "lucide-react";
import { buildPdf, type PageSize, type PdfImage } from "../../logic/imageToPdf";

type Item = {
  id: number;
  file: File;
  previewUrl: string;
};

const MAX_SIDE = 4000;
const JPEG_QUALITY = 0.92;
const MARGIN_POINTS: Record<string, number> = { none: 0, small: 18, big: 36 };

// Draws the image on a white canvas (so transparency does not turn black)
// and re-encodes it as JPEG, which also applies any EXIF rotation.
async function toPdfImage(file: File): Promise<PdfImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("Could not encode image");
  return { jpeg: new Uint8Array(await blob.arrayBuffer()), width, height };
}

export function ImageToPdf() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [pageSize, setPageSize] = useState<PageSize>("a4");
  const [margin, setMargin] = useState("small");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const nextId = useRef(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<Item[]>([]);
  itemsRef.current = items;

  // Release preview URLs on unmount.
  useEffect(
    () => () => itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl)),
    [],
  );

  const addFiles = useCallback(
    (fileList: FileList | File[] | null) => {
      if (!fileList) return;
      const files = Array.from(fileList);
      const images = files.filter((file) => file.type.startsWith("image/"));
      setError(images.length < files.length ? t("imageToPdf.skipped") : null);
      if (images.length === 0) return;
      setItems((prev) => [
        ...prev,
        ...images.map((file) => ({
          id: nextId.current++,
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ]);
    },
    [t],
  );

  const removeItem = useCallback((id: number) => {
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const moveItem = useCallback((index: number, direction: -1 | 1) => {
    setItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setItems((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    setError(null);
  }, []);

  const handleCreate = useCallback(async () => {
    if (items.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const images: PdfImage[] = [];
      for (const item of items) {
        images.push(await toPdfImage(item.file));
      }
      const pdf = buildPdf(images, {
        pageSize,
        margin: MARGIN_POINTS[margin] ?? 0,
      });
      const url = URL.createObjectURL(new Blob([pdf as BlobPart], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "images.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError(t("imageToPdf.failed"));
    } finally {
      setBusy(false);
    }
  }, [items, pageSize, margin, t]);

  return (
    <div className="image-to-pdf">
      <div
        className={`i2p-dropzone ${dragging ? "dragging" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <ImagePlus size={32} aria-hidden="true" />
        <strong>{t("imageToPdf.dropTitle")}</strong>
        <span>{t("imageToPdf.dropHint")}</span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {error && <p className="i2p-error">{error}</p>}

      {items.length > 0 && (
        <ul className="i2p-list">
          {items.map((item, index) => (
            <li className="i2p-item" key={item.id}>
              <img className="i2p-thumb" src={item.previewUrl} alt="" />
              <span className="i2p-name">
                {index + 1}. {item.file.name}
              </span>
              <div className="i2p-item-actions">
                <button
                  type="button"
                  className="i2p-icon-btn"
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  aria-label={t("imageToPdf.moveUp")}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type="button"
                  className="i2p-icon-btn"
                  onClick={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  aria-label={t("imageToPdf.moveDown")}
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  type="button"
                  className="i2p-icon-btn"
                  onClick={() => removeItem(item.id)}
                  aria-label={t("imageToPdf.remove")}
                >
                  <X size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="i2p-options">
        <label className="i2p-option">
          <span>{t("imageToPdf.pageSize")}</span>
          <select value={pageSize} onChange={(e) => setPageSize(e.target.value as PageSize)}>
            <option value="a4">A4</option>
            <option value="letter">Letter</option>
            <option value="fit">{t("imageToPdf.fit")}</option>
          </select>
        </label>
        <label className="i2p-option">
          <span>{t("imageToPdf.margin")}</span>
          <select value={margin} onChange={(e) => setMargin(e.target.value)}>
            <option value="none">{t("imageToPdf.marginNone")}</option>
            <option value="small">{t("imageToPdf.marginSmall")}</option>
            <option value="big">{t("imageToPdf.marginBig")}</option>
          </select>
        </label>
      </div>

      <div className="i2p-actions">
        <button
          type="button"
          className="i2p-primary-btn"
          onClick={handleCreate}
          disabled={items.length === 0 || busy}
        >
          <Download size={16} />
          {busy ? t("imageToPdf.creating") : t("imageToPdf.create")}
        </button>
        <button type="button" className="i2p-secondary-btn" onClick={clearAll} disabled={items.length === 0}>
          <Trash2 size={16} />
          {t("imageToPdf.clear")}
        </button>
      </div>

      <p className="i2p-privacy">{t("imageToPdf.privacy")}</p>
    </div>
  );
}
